/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';

import loadNotes from '@/loaders/NotesLoader';
import OrderPackageNoteFileUploadTmpl from '@/fileupload/OrderPackageNoteFileUploadTmpl.hbs';
import lazyLoadView from '@/util/lazyLoadView';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import showModal from '@/util/showModal';
import SearchUsersModalView from '@/views/SearchUsersModalView/SearchUsersModalView';
import SearchEPCUsersModalView from '@/views/SearchEPCUsersModalView/SearchEPCUsersModalView';
import OrderPackageStatuses from '@/enums/OrderPackageStatuses';

import SideNavSectionView from '../../../common/views/SideNavSectionView';
import OrderPackageHeaderView from '../../views/OrderPackageHeaderView/OrderPackageHeaderView';
import loadOrderPackageSelectOrdersView from '../../loaders/OrderPackageSelectOrdersViewLoader';
import loadOrderPackageDeliveryScheduleView from '../../loaders/OrderPackageDeliveryScheduleViewLoader';
import loadOrderPackageAttachmentsView from '../../loaders/OrderPackageAttachmentsViewLoader';
import loadOrderPackageInformationView from '../../loaders/OrderPackageInformationViewLoader';
import loadOrderPackageDecisionView from '../../loaders/OrderPackageDecisionViewLoader';
import GetOrderPackageNotes from '../../services/GetOrderPackageNotes';
import SubmitOrderPackageNote from '../../services/SubmitOrderPackageNote';
import SubmitOrderPackage from '../../services/SubmitOrderPackage';
import OrderPackageAccess from '../../utils/OrderPackageAccess';
import ReassignOrderPackageCRS from '../../services/ReassignOrderPackageCRS';
import ReassignOrderPackageEPC from '../../services/ReassignOrderPackageEPC';
import GetPackagedCustomerOrdersByOrderPackageID from '../../services/GetPackagedCustomerOrdersByOrderPackageID';
import DeleteDraftOrderPackage from '../../services/DeleteDraftOrderPackage';
import UnclaimOrderPackage from '../../services/UnclaimOrderPackage';
import HideOrderPackage from '../../services/HideOrderPackage';
import ApproveReturnOrderPackage from '../../services/ApproveReturnOrderPackage';

import './OrderPackagePage.css';

import tmplOrderPackagePage from './OrderPackagePage.hbs';

const OrderPackagePage = View.extend({
  template: tmplOrderPackagePage,

  className: 'order-package-page container-fluid',

  ui: {
    sections: '.sections-container',
    orderPackageHeader: '.order-package-header'
  },

  regions: {
    sections: '@ui.sections',
    orderPackageHeader: '@ui.orderPackageHeader'
  },

  childViewEvents: {
    reload: 'reloadEditable',
    print: 'printOrder',
    unclaim: 'onClickUnclaim',
    'reassign:crs': 'onReassignCRS',
    'reassign:epc': 'onReassignEPC',
    'delete:draft': 'onDeleteDraft',
    hide: 'onClickHide'
  },

  onRender() {
    this.showHeaderView();
    this.showSideNavSectionView();
  },

  showHeaderView() {
    const orderPackage = this.model.get('orderPackage');
    this.isEditing = false;
    const awPackageStatusID = this.model.get('awPackageStatusID')
    let isAWPackageEditable = true;

    if (awPackageStatusID){
      isAWPackageEditable = (awPackageStatusID < 4 || awPackageStatusID == 8 || awPackageStatusID == 11)
    }

    const { isCRSOwner, isEPCOwner } = this.options;

    const showViewEditBtn = OrderPackageAccess.isEditViewButtonVisible(orderPackage.statusID, isCRSOwner, isEPCOwner, isAWPackageEditable);
    const showReassignCRSBtn = OrderPackageAccess.isCRSReassignable(
      orderPackage.statusID,
      isCRSOwner,
      isEPCOwner
    );
    const showReassignEPCBtn = OrderPackageAccess.isEPCReassignable(orderPackage.statusID, isEPCOwner);
    // const showPrintBtn = OrderPackageAccess.isPrintable(orderPackage.orderFormTypeID);
    const showPrintBtn = true;
    const showDeleteButton = OrderPackageAccess.isDeleteable(orderPackage.statusID, isCRSOwner);
    const showUnClaimButton = OrderPackageAccess.isUnClaimButtonVisible(orderPackage);
    const showHideButton = OrderPackageAccess.isHideButtonVisible(orderPackage.statusID);

    this.showChildView(
      'orderPackageHeader',
      new OrderPackageHeaderView({
        showViewEditBtn,
        showReassignCRSBtn,
        showReassignEPCBtn,
        showPrintBtn,
        showDeleteButton,
        showUnClaimButton,
        showHideButton,
        isEditing: this.isEditing,
        model: new Backbone.Model(orderPackage)
      })
    );
  },

  showSideNavSectionView() {
    const orderPackage = this.model.get('orderPackage');

    const { activeSectionId, orderPackageID, isCRSOwner, isEPCOwner } = this.options;
    const statusID = orderPackage.statusID;

    const awPackageId = orderPackage.awPackageID;
    const awPackageStatusID = this.model.get('awPackageStatusID')
    let isAWPackageEditable = true;
    if (awPackageStatusID){
      isAWPackageEditable = (awPackageStatusID < 4 || awPackageStatusID == 8 || awPackageStatusID == 11)
    }
    //Determine user access to order package based on role, ownership of order package, status, awPackageStatus, and tab
    const isEditable = OrderPackageAccess.isEditModeActive(statusID, isCRSOwner, isEPCOwner, this.isEditing, isAWPackageEditable);
    const isSavable = OrderPackageAccess.isSavable(statusID, isCRSOwner, isEPCOwner, this.isEditing, isAWPackageEditable);
    const isSubmittable = OrderPackageAccess.isSubmittable(statusID, isCRSOwner, isEPCOwner);

    const isNotesEditable = OrderPackageAccess.isNotesEditable(statusID);

    const isDecisionRenderable = OrderPackageAccess.isDecisionRenderable(statusID, isEPCOwner);
    const isDecisionEditable = OrderPackageAccess.isDecisionEditable(statusID, isEPCOwner);
    const isDecisionSavable = OrderPackageAccess.isDecisionSavable(statusID, isEPCOwner);
    const isDecisionSubmittable = OrderPackageAccess.isDecisionSubmittable(statusID, isCRSOwner, isEPCOwner);
    this.showChildView(
      'sections',
      new SideNavSectionView({
        useNavigationButtons: true,
        editable: isEditable,
        activeSectionId,
        onSubmit: this.onSubmit.bind(this),
        model: new Backbone.Model({
          returnLabel: 'Return to Dashboard',
          returnLink: '#dashboard',
          packageLabel: awPackageId ? 'Open AW Package' : null,
          packageLink: `#aw-package/${awPackageId}`,
          submitButtonText: statusID === '9' ? 'Submit to Complete Order Package' : null
        }),
        collection: new Backbone.Collection([
          {
            id: 'selectOrders',
            title: 'Select Orders',
            editable: isEditable,
            isSavable,
            showSubmitButton: isSubmittable,
            loader: loadOrderPackageSelectOrdersView,
            loaderOptions: { orderPackageID, editable: isEditable },
            view: lazyLoadView(() =>
              loadOrderPackageSelectOrdersView({
                orderPackageID,
                editable: isEditable
              })
            )
          },
          {
            id: 'orderPackageDeliverySchedule',
            title: 'Delivery Schedule',
            editable: isEditable,
            isSavable: false,
            showSubmitButton: isSubmittable,
            loader: loadOrderPackageDeliveryScheduleView,
            loaderOptions: { orderPackageID, editable: isEditable, orderPackage },
            view: lazyLoadView(() =>
              loadOrderPackageDeliveryScheduleView({ orderPackageID, editable: isEditable, orderPackage })
            )
          },
          {
            id: 'orderPackageAttachments',
            title: 'Attachments',
            editable: false,
            isSavable: false,
            showSubmitButton: isSubmittable,
            loader: loadOrderPackageAttachmentsView,
            loaderOptions: { orderPackageID, editable: false },
            view: lazyLoadView(() => loadOrderPackageAttachmentsView({ orderPackageID, isEditable: false })), //??
            tooltip: {
              text: 'If updates are needed, find the order that requires attachment updates below and click the order ID number to be navigated to the order to edit.',
              placement: 'right'
            }
          },
          {
            id: 'orderPackageInformation',
            title: 'Package Info',
            editable: isEditable,
            isSavable,
            showSubmitButton: isSubmittable,
            loader: loadOrderPackageInformationView,
            loaderOptions: { orderPackageID, editable: isEditable },
            view: lazyLoadView(() => loadOrderPackageInformationView({ orderPackageID, editable: isEditable }))
          },
          {
            id: 'orderPackageNotes',
            title: 'Package Notes',
            editable: isNotesEditable,
            isSavable: false,
            showSubmitButton: isSubmittable,
            loader: loadNotes,
            loaderOptions: {
              objectTypeId: 'ORDER_PACKAGE',
              editable: isNotesEditable,
              metadata: {
                orderPackageID
              },
              objectId: orderPackageID,
              getNotesService: GetOrderPackageNotes,
              submitNoteService: SubmitOrderPackageNote,
              submitNoteAttachmentService: OrderPackageNoteFileUploadTmpl,
              pathToFileId:
                'html.head.script.Envelope.Body.UploadOrderPackageNoteAttachmentResponse.AttachmentID.__text'
            },
            view: lazyLoadView(() =>
              loadNotes({
                editable: isNotesEditable,
                metadata: {
                  orderPackageID
                },
                objectId: orderPackageID,
                getNotesService: GetOrderPackageNotes,
                submitNoteService: SubmitOrderPackageNote,
                submitNoteAttachmentService: OrderPackageNoteFileUploadTmpl,
                pathToFileId:
                  'html.head.script.Envelope.Body.UploadOrderPackageNoteAttachmentResponse.AttachmentID.__text'
              })
            )
          },
          {
            id: 'orderPackageDecision',
            title: 'Decision',
            editable: isDecisionEditable,
            renderable: isDecisionRenderable,
            isSavable: isDecisionSavable,
            showSubmitButton: isDecisionSubmittable,
            loader: loadOrderPackageDecisionView,
            loaderOptions: {
              orderPackageID,
              editable: isDecisionEditable
            },
            view: lazyLoadView(() =>
              loadOrderPackageDecisionView({
                orderPackageID,
                editable: isDecisionEditable
              })
            )
          }
        ])
      })
    );
  },

  showSubmissionErrorMessage() {
    showErrorMessage({
      errorCode: 'submissionError',
      errorTitle: 'Submission Error',
      errorMessage: 'An error occurred during submission. Try again or contact an administrator if your issue persists.'
    });
  },

  showSubmissionSuccessMessage() {
    showSuccessMessage({
      code: 'submitSuccess',
      title: 'Submit Success',
      message: 'Order Package Submitted.',
      hideAfter: 5
    });
  },

  showReassignmentErrorMessage() {
    showErrorMessage({
      errorCode: 'reassignmentError',
      errorTitle: 'Reassignment Error',
      errorMessage:
        'An error occurred during reassignment. Try again or contact an administrator if your issue persists.'
    });
  },

  showDraftDeletedErrorMessage() {
    showErrorMessage({
      errorCode: 'draftDeletedError',
      errorTitle: 'Draft Deleted Error',
      errorMessage: 'An error occurred during deletion. Try again or contact an administrator if your issue persists.'
    });
  },

  showDraftDeletedSuccessMessage() {
    showSuccessMessage({
      code: 'deleteSuccess',
      title: 'Delete Success',
      message: 'Order Package Draft Deleted.',
      hideAfter: 5
    });
  },

  getActiveSectionId() {
    let activeSectionID = undefined;
    try {
      const sections = this.getChildView('sections');
      const activeLinkIndex = parseInt(sections.linkCollectionView.getActiveLinkIndex());
      const activeModel = sections.linkCollectionView.collection.at(activeLinkIndex);
      activeSectionID = activeModel.get('id');
    } catch (err) {
      // Ignore
    }
    return activeSectionID;
  },

  async onReassignCRS() {
    const user = await showModal({
      title: 'Reassign CRS',
      view: new SearchUsersModalView(),
      dialogClasses: 'modal-lg'
    });

    try {
      await ReassignOrderPackageCRS({
        orderPackageId: this.model.get('orderPackage')?.id,
        internalUserId: user.id
      });
      this.getChildView('orderPackageHeader').setAssignedCRS(user.userName);
    } catch (err) {
      this.showReassignmentErrorMessage();
    }
  },

  async onReassignEPC() {
    const user = await showModal({
      title: 'Reassign EPC',
      view: new SearchEPCUsersModalView(),
      dialogClasses: 'modal-lg'
    });

    try {
      await ReassignOrderPackageEPC({
        orderPackageId: this.model.get('orderPackage')?.id,
        internalUserId: user.id
      });
      this.getChildView('orderPackageHeader').setAssignedEPC(user.userName);
    } catch (err) {
      this.showReassignmentErrorMessage();
    }
  },

  async printOrder() {
    Radio.channel('navigate').request('show:order-package:print', this.model.get('orderPackage').id);
  },

  async validate(orderPackageId) {
    const packagedOrders = await GetPackagedCustomerOrdersByOrderPackageID(orderPackageId);
    const hasPackagedOrders = packagedOrders.length > 0;

    if (!hasPackagedOrders) this.showNoOrdersError();

    return hasPackagedOrders;
  },

  showNoOrdersError() {
    showErrorMessage({
      errorCode: 'noOrdersPackagedError',
      errorTitle: 'No Orders Added',
      errorMessage: 'You must add one or more orders to the order package before submitting.'
    });
  },

  async onDeleteDraft() {
    try {
      await DeleteDraftOrderPackage(this.model.get('orderPackage')?.id);
      this.showDraftDeletedSuccessMessage();
      Radio.channel('navigate').request('show:int:dashboard');
    } catch (err) {
      this.showDraftDeletedErrorMessage();
    }
  },

  async onClickUnclaim() {
    try {
      await UnclaimOrderPackage(this.options.orderPackageID);
      Radio.channel('navigate').request('show:int:dashboard');
      this.showOrderUnclaimedSuccessMessage();
    } catch (err) {
      this.showOrderUnclaimedErrorMessage();
    }
  },

  async onSubmit() {
    const orderPackageId = this.options.orderPackageID;
    const epcDecisionExplanation = this.model.get('orderPackage').epcDecisionExplanation;

    if (!(await this.validate(orderPackageId))) {
      return;
    }

    const statusID = this.model.get('orderPackage').statusID;
    const isCRSOwner = this.options.isCRSOwner;
    const isEPCOwner = this.options.isEPCOwner;
    const isSubmittable = OrderPackageAccess.isSubmittable(statusID, isCRSOwner, isEPCOwner);

    //only CRSs can submit order packages (EPCs only save and Approve/Reject)
    try {
      if (statusID === OrderPackageStatuses.SUBMITTED_TO_VENDOR) {
        await ApproveReturnOrderPackage(orderPackageId, OrderPackageStatuses.COMPLETE, epcDecisionExplanation);
        await Radio.channel('navigate').request('show:int:order-package', orderPackageId);
      } else if (isSubmittable) {
        await SubmitOrderPackage(this.options.orderPackageID);
        this.showSubmissionSuccessMessage();
        await Radio.channel('navigate').request('show:int:dashboard');
      }
    } catch (err) {
      if (err?.message.string?.includes('Unauthorized')) {
        this.showUserUnauthorizedErrorMessage();
      } else {
        this.showSubmissionErrorMessage();
      }
    }
  },

  reloadEditable(isEditing) {
    this.options.activeSectionId = this.getActiveSectionId();
    this.isEditing = isEditing;
    this.showSideNavSectionView();
  },

  async onClickHide() {
    try {
      await HideOrderPackage(this.options.orderPackageID);
      const orderPackageHeaderView = this.getChildView('orderPackageHeader');
      orderPackageHeaderView.model.set('status', 'Hidden');
      orderPackageHeaderView.options.showHideButton = false;
      orderPackageHeaderView.render();
      this.showHideSuccessMessage();
    } catch (err) {
      this.showHideErrorMessage();
    }
  },

  showHideSuccessMessage() {
    showSuccessMessage({
      code: 'hideSuccess',
      title: 'Hide Success',
      message: 'Order Package Successfully Hidden.',
      hideAfter: 10
    });
  },

  showHideErrorMessage() {
    showErrorMessage({
      errorCode: 'hideError',
      errorTitle: 'Hide Error',
      hideAfter: 10,
      errorMessage:
        'An error occurred while hiding this order package. Try again or contact an administrator if your issue persists.'
    });
  },

  showOrderUnclaimedErrorMessage() {
    showErrorMessage({
      errorCode: 'unclaimError',
      errorTitle: 'Unclaim Order Error',
      errorMessage:
        'An error occurred while unclaiming this order package. Try again or contact an administrator if your issue persists.',
      hideAfter: 10
    });
  },

  showOrderUnclaimedSuccessMessage() {
    showSuccessMessage({
      code: 'unclaimSuccess',
      title: 'Unclaim Success',
      message: 'Order Package Unclaimed.',
      hideAfter: 5
    });
  },

  showUserUnauthorizedErrorMessage() {
    showErrorMessage({
      errorCode: 'userNotAssociated',
      errorTitle: 'Unauthorized',
      errorMessage: 'User is not associated with Order Package.',
      hideAfter: 10
    });
  }
});

export default OrderPackagePage;
